<template>
  <PageHeader
    title="External Dealership Systems"
    breadcrumb
    :items="[
      { label: $t('ui.entities.setting', 2), to: '/settings' },
      { label: 'Security' },
      { label: 'External Dealership Systems' }
    ]"
  >
    <template #right>
      <Button
        v-if="$can('manage_external_systems')"
        :label="$t('ui.crud.add', { item: $t('ui.common.account') })"
        severity="primary"
        icon="plus"
        @click="addAccount"
      />
    </template>
  </PageHeader>

  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      :value="systemRecordsComputed"
      removable-sort
      sort-field="id"
      :rows="20"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-settings-security-external-authenticator-codes"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :global-filter-fields="['id', 'systemName', 'username', 'dealershipNames']"
      :loading="systemRecordsApiRequest.isLoading.value"
    >
      <template #empty>No records found</template>

      <!-- Header -->
      <template #header>
        <div class="flex justify-between">
          <!-- Left side: search + clear -->
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>

          <!-- Right side: show countdown if valid, else show Refresh -->
          <template v-if="!codesExpired">
            <Button
              :label="'2FA Codes expiring in ' + timeLeft + 's'"
              severity="info"
              disabled
              outlined
            />
          </template>
          <template v-else>
            <Button
              label="Refresh Codes"
              severity="primary"
              outlined
              icon="refresh"
              @click="fetchData"
            />
          </template>
        </div>
      </template>
      <!-- End Header -->

      <Column
        field="systemName"
        header="System"
        :sortable="true"
      >
        <template #body="{ data }">
          <a
            v-if="data.baseUrl"
            :href="data.baseUrl"
            target="_blank"
            class="flex gap-2 link"
          >
            <div>
              {{ getDictionaryItem('externalSystems', data.systemName)?.label || data.systemName }}
            </div>
            <Icon type="arrow-up-right-from-square" />
          </a>
          <div v-else>
            {{ getDictionaryItem('externalSystems', data.systemName)?.label || data.systemName }}
          </div>
        </template>
      </Column>

      <Column
        field="dealerships"
        header="Dealerships"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <ChipList
            v-if="data.dealerships.length"
            :items="
              data.dealerships.map(d => {
                return { label: d.name, size: 'sm' }
              })
            "
            :overflow-chip-props="{ size: 'sm' }"
          />
          <div v-else>-</div>
        </template>
        <template #filter="{ filterModel }">
          <Select
            v-model="filterModel.value"
            mode="multiple"
            :chips="false"
            :options="dealershipsAllowed"
            searchable
            value-key="id"
            label-key="name"
            class="min-w-64"
            placeholder="All"
          >
            <template #option="slotProps">
              {{ slotProps.option.name }}
            </template>
          </Select>
        </template>
      </Column>

      <Column
        field="username"
        header="Username"
        :sortable="true"
      >
        <template #body="{ data }">
          <div class="flex gap-2">
            {{ data.username }}
            <CopyText
              show-icon
              :explicit-text-to-copy="data.username"
              class="text-tertiary"
            ></CopyText>
          </div>
        </template>
      </Column>

      <Column
        field="password"
        header="Password"
      >
        <template #body="{ data }">
          <div
            v-if="data.password"
            class="flex gap-2 items-center"
          >
            <div class="flex gap-[2px]">
              <div
                v-for="num in 8"
                :key="num"
              >
                <Icon
                  type="circle"
                  class="text-[0.4rem]"
                />
              </div>
            </div>
            <CopyText
              show-icon
              :explicit-text-to-copy="data.password"
              class="text-tertiary"
            ></CopyText>
            <div>
              <Button
                v-tippy="'Replace password'"
                severity="secondary"
                icon="pencil"
                plain
                size="sm"
                @click="openPasswordRegenerateModal(data)"
              />
            </div>
          </div>
          <div v-else>-</div>
        </template>
      </Column>

      <!-- Show TOTP code if not expired, otherwise "Expired" -->
      <Column
        field="totp"
        header="2FA Code"
        class="whitespace-nowrap"
      >
        <template #body="{ data }">
          <div v-if="data.totp">
            <CopyText
              v-if="!codesExpired"
              show-icon
              class="flex font-bold"
            >
              {{ data.totp }}
            </CopyText>
            <span v-else>Expired</span>
          </div>
          <div v-else>-</div>
        </template>
      </Column>

      <Column>
        <template #body="{ data }">
          <div class="flex">
            <Button
              v-if="$can('manage_external_systems')"
              v-tippy="'Edit'"
              plain
              icon="cog"
              severity="secondary"
              @click="editAccount(data)"
            />
            <Button
              v-if="$can('manage_external_systems')"
              v-tippy="'Delete'"
              plain
              icon="trash"
              severity="danger"
              :is-loading="deleteAccountRequest.isLoading.value"
              @click="deleteAccount(data.id)"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </Card>

  <Modal
    :is-open="accountFormModalIsOpen"
    :title="isNewAccount ? 'New External Account' : 'Edit External Account'"
    icon="key"
    :buttons="['ok', 'close']"
    :has-unsaved-changes="accountFormHasUnsavedChanges"
    close-text="Cancel"
    :ok-text="isNewAccount ? 'Add Account' : 'Save'"
    :show-loader-on-confirm="true"
    :is-confirming="isNewAccount ? newAccountApiRequest.isLoading.value : editAccountApiRequest.isLoading.value"
    overflow-behaviour="visible"
    @close-modal="((accountFormModalIsOpen = false), (accountFormHasUnsavedChanges = false), resetVuelidate())"
    @ok-modal="submitForm()"
  >
    <div class="grid grid-cols-2 gap-x-6">
      <FormGroup
        label="System"
        :is-required="v$.systemName.required?.$invalid"
      >
        <Select
          v-model="accountFormState.systemName"
          icon="desktop"
          :options="systemNameOptions"
          searchable
          value-key="key"
          label-key="label"
          :has-error="v$.systemName.$error"
        />
        <InputError :has-error="v$.systemName.$error">{{ v$.systemName.$errors[0]?.$message }}</InputError>
      </FormGroup>
      <FormGroup
        label="Username"
        :is-required="v$.username.required?.$invalid"
      >
        <Input
          v-model="accountFormState.username"
          icon="at"
          :placeholder="isNewAccount ? 'john@example.com' : ''"
          :has-error="v$.username.$error"
        />
        <InputError :has-error="v$.username.$error">{{ v$.username.$errors[0]?.$message }}</InputError>
      </FormGroup>
      <div class="gap-x-6">
        <FormGroup
          label="2FA Secret Key"
          info="Copy over the secret key rather than scanning a QR code. If you're unsure of the secret key, contact your manager"
          :is-required="v$.totpSecretKey.required?.$invalid"
        >
          <Input
            v-model="accountFormState.totpSecretKey"
            icon="key"
            :has-error="v$.totpSecretKey.$error"
          />
          <InputError :has-error="v$.totpSecretKey.$error">{{ v$.totpSecretKey.$errors[0]?.$message }}</InputError>
        </FormGroup>
      </div>
      <div class="gap-x-6">
        <FormGroup
          label="System URL"
          :is-required="v$.baseUrl.required?.$invalid"
        >
          <Input
            v-model="accountFormState.baseUrl"
            icon="globe-pointer"
            :placeholder="isNewAccount ? 'https://' : ''"
            :has-error="v$.baseUrl.$error"
          />
          <InputError :has-error="v$.baseUrl.$error">{{ v$.baseUrl.$errors[0]?.$message }}</InputError>
        </FormGroup>
      </div>
      <div class="col-span-2 gap-x-6">
        <FormGroup
          label="Dealerships"
          :is-required="v$.dealerships.required?.$invalid"
        >
          <Select
            v-model="accountFormState.dealershipIds"
            :options="dealershipsAllowed"
            value-key="id"
            label-key="name"
            auto-expand
            searchable
            mode="multiple"
            class="max-w-[600px]"
            chips-displayed="10"
            :has-error="v$.dealerships.$error"
          />
          <InputError :has-error="v$.dealerships.$error">{{ v$.dealerships.$errors[0]?.$message }}</InputError>
        </FormGroup>
      </div>
    </div>
    <Alert
      v-if="isNewAccount"
      title="The generated password will be shown after account creation"
      icon="lock"
      severity="info"
      :show-close-button="false"
      :show-dismiss-button="false"
    />
  </Modal>

  <Modal
    :is-open="newPasswordModalIsOpen"
    title="Replace Password"
    description="Generate or set a new password"
    icon="lock"
    :buttons="[]"
    :is-confirming="false"
    min-width="300"
    overflow-behaviour="visible"
    @close-modal="closePasswordModal()"
  >
    <div
      v-if="isManualPassword"
      class="flex flex-col gap-6 items-center"
    >
      <div class="w-full items-start">
        <Input
          v-model="newManualPassword.password"
          class="w-full"
          icon="lock"
          placeholder="Example123!"
          :is-required="vPassword$.password.required?.$invalid"
          :has-error="vPassword$.password.$error"
        />

        <InputError :has-error="vPassword$.password.$error">{{ vPassword$.password.$errors[0]?.$message }}</InputError>
      </div>

      <div
        v-if="newManualPassword.password"
        class="flex gap-2 w-full max-w-[300px]"
      >
        <Icon
          type="triangle-exclamation"
          severity="warning"
        />
        <div class="text-secondary">Don't forget to update the account password on the external system</div>
      </div>
      <div class="flex flex-col gap-2 w-full items-center">
        <Button
          label="Save"
          class="w-full"
          :is-loading="savePasswordRequest.isLoading.value"
          @click="savePassword()"
        />
        <div
          class="link"
          @click="isManualPassword = false"
        >
          or Generate & Replace
        </div>
      </div>
    </div>

    <div
      v-else
      class="flex flex-col gap-6 items-center"
    >
      <div class="w-[300px] h-32 relative border border-primary rounded-md flex justify-center items-center">
        <div
          v-if="!newGeneratedPassword"
          class="absolute z-10 backdrop-blur-[0.2rem] bg-primary/30 rounded-lg w-[90%] h-[90%]"
        ></div>
        <div class="flex gap-2 items-center">
          <div class="text-xl">
            {{ newGeneratedPassword ? newGeneratedPassword : newPasswordAccount.password }}
          </div>
          <CopyText
            v-if="newGeneratedPassword"
            :explicit-text-to-copy="newGeneratedPassword"
            show-icon
          />
        </div>
      </div>
      <div
        v-if="newGeneratedPassword"
        class="flex gap-2 w-full max-w-[300px]"
      >
        <Icon
          type="triangle-exclamation"
          severity="warning"
        />
        <div class="text-secondary">Don't forget to update the account password on the external system</div>
      </div>
      <div
        v-else
        class="flex flex-col gap-2 w-full items-center"
      >
        <Button
          label="Generate & Replace"
          class="w-full"
          :is-loading="generatePasswordRequest.isLoading.value"
          @click="regeneratePassword()"
        />
        <div
          class="link"
          @click="isManualPassword = true"
        >
          or Set Password Manually
        </div>
      </div>
    </div>
  </Modal>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'

import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import Button from '@/components/button/Button.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Input from '@/components/forms/Input.vue'
import Modal from '@/components/modal/Modal.vue'
import Icon from '@/components/icon/Icon.vue'
import Select from '@/components/forms/Select.vue'
import ChipList from '@/components/chip/ChipList.vue'
import Alert from '@/components/alert/Alert.vue'
import CopyText from '@/components/CopyText.vue'
import InputError from '@/components/forms/InputError.vue'

import useApiRequest from '@/composables/useApiRequest'
import { getDictionaryItem, getDictionaryOptions } from '@/dictionary'

import { isEqual } from 'lodash'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode, FilterService } from 'primevue/api'
import { alertToast } from '@/utilities/notification'
import { useUserStore } from '@/stores/UserStore.ts'
import Swal from 'sweetalert2'

import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

const accountFormStateDefault = ref({
  id: null,
  username: null,
  systemName: null,
  password: null,
  totpSecretKey: null,
  baseUrl: null,
  agreementUrl: null,
  dealerships: [],
  dealershipIds: []
})

const accountFormStateInit = ref(null)
const accountFormState = ref(null)

const accountFormModalIsOpen = ref(false)
const accountFormHasUnsavedChanges = ref(false)
const isNewAccount = ref(true)
const userStore = useUserStore()
const dealershipsAllowed = userStore.accessAllowed.dealerships.map(d => ({
  id: d.id,
  name: d.name
}))

const systemNameOptions = getDictionaryOptions('externalSystems')

// Table data
const systemRecords = ref([])
const systemRecordsApiRequest = useApiRequest()

// Filters

onMounted(() => {
  FilterService.register(dealershipsFilter.value, (value, filter) => {
    if (!filter.length) {
      return true
    }

    return value.some(dealership => filter.includes(dealership.id))
  })
})

const dealershipsFilter = ref('Dealerships Filter')

const filters = ref(null)

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    dealerships: { value: null, matchMode: dealershipsFilter.value },
    id: { value: null, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

const clearFilters = () => {
  initFilters()
}

// We'll store the next TOTP boundary (e.g. top/half of the minute)
const expiryTimestamp = ref(0)

// We'll also update 'nowSeconds' every second
// so we can compute how long until we hit expiryTimestamp
const nowSeconds = ref(0)
let timer = null

onMounted(() => {
  fetchData()
  nowSeconds.value = Math.floor(Date.now() / 1000)
  timer = setInterval(() => {
    nowSeconds.value = Math.floor(Date.now() / 1000)
  }, 1000)
})

onUnmounted(() => {
  clearInterval(timer)
})

function editAccount(account) {
  isNewAccount.value = false
  accountFormStateInit.value = {
    ...account,
    dealershipIds: account.dealerships.map(d => {
      return d.id
    })
  }
  accountFormState.value = { ...accountFormStateInit.value }
  accountFormModalIsOpen.value = true
}

function addAccount() {
  isNewAccount.value = true
  accountFormStateInit.value = { ...accountFormStateDefault.value }
  accountFormState.value = { ...accountFormStateInit.value }
  accountFormModalIsOpen.value = true
}

const deleteAccountRequest = useApiRequest()
function deleteAccount(id) {
  Swal.fire({
    title: 'Are you sure?',
    icon: 'warning',
    reverseButtons: true,
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes, delete this account',
    confirmButtonColor: 'var(--bg-error-solid)',
    cancelButtonText: 'Cancel'
  }).then(function (response) {
    if (response.isConfirmed) {
      try {
        deleteAccountRequest.send({ endpoint: `/v1/external-systems/${id}`, method: 'DELETE' }).then(response => {
          if (response.success) {
            alertToast('Deleted account', null, 'success')
            const system = systemRecords.value.find(system => system.id == id)
            const index = systemRecords.value.indexOf(system)
            if (index > -1) {
              systemRecords.value.splice(index, 1)
            }
            // fetchData()
          } else {
            alertToast('Failed to delete account', response.message, 'error')
          }
        })
      } catch (error) {
        console.error('Failed to delete account', error)
        alertToast('Failed to delete account', error.message, 'error')
      }
    }
  })
}

const generatePasswordRequest = useApiRequest()
const savePasswordRequest = useApiRequest()
const newGeneratedPassword = ref(null)
const newManualPassword = ref({ password: null })
const isManualPassword = ref(false)
const newPasswordAccount = ref(null)
const newPasswordModalIsOpen = ref(false)
// const newPasswordConfirmationModalIsOpen = ref(false)

function openPasswordRegenerateModal(system) {
  newPasswordModalIsOpen.value = true
  newManualPassword.value.password = null
  newPasswordAccount.value = system
}

function regeneratePassword() {
  try {
    generatePasswordRequest
      .send({
        endpoint: `/v1/external-systems/${newPasswordAccount.value.id}/update-password`,
        method: 'POST'
      })
      .then(response => {
        if (response.success) {
          newGeneratedPassword.value = response.data.newPassword
          const system = systemRecords.value.find(system => system.id == newPasswordAccount.value.id)
          system.password = newGeneratedPassword.value
        } else {
          alertToast('Failed to regenerate password', response.message, 'error')
        }
      })
  } catch (error) {
    console.error('Failed to regenerate password', error)
    alertToast('Failed to regenerate password', error.message, 'error')
  }
  resetPasswordVuelidate()
}

const passwordInputRules = {
  password: { required }
}

const vPassword$ = useVuelidate(passwordInputRules, newManualPassword)

async function savePassword() {
  const isFormCorrect = await vPassword$.value.$validate()
  if (!isFormCorrect) {
    return
  }

  // TEMP for updating passwords
  const dataToPost = {
    password: newManualPassword.value.password
  }

  try {
    savePasswordRequest
      .send({
        method: 'POST',
        endpoint: `/v1/external-systems/${newPasswordAccount.value.id}/update-password`,
        data: JSON.stringify(dataToPost)
      })
      .then(response => {
        if (!response.success) {
          alertToast('Error', response.message, 'error')
        } else {
          alertToast('Success!', 'Password updated', 'success')
          const account = systemRecords.value.find(record => record.id == newPasswordAccount.value.id)
          account.password = newManualPassword.value.password
          newPasswordModalIsOpen.value = false
          closePasswordModal()
        }
      })
  } catch (error) {
    console.error('Failed to save password', error)
    alertToast('Failed to save password', error.message, 'error')
  }
  resetPasswordVuelidate()
}

function closePasswordModal() {
  newPasswordModalIsOpen.value = false
  newGeneratedPassword.value = null
  newManualPassword.value.password = null
  newPasswordAccount.value = null
  isManualPassword.value = false
  resetPasswordVuelidate()
}

// Fetch codes from the API
function fetchData() {
  systemRecordsApiRequest.send({ endpoint: '/v1/external-systems' }).then(response => {
    systemRecords.value = response.data

    // Set an expiry time at the *next* TOTP boundary
    const fetchTime = Math.floor(Date.now() / 1000)
    const remainder = fetchTime % 30
    // e.g. if remainder=15, next boundary is fetchTime + (30 - 15)
    expiryTimestamp.value = fetchTime + (30 - remainder)

    // If remainder is 0, it means we're exactly at a boundary, so let's just set expiry 30s from now
    // but the same formula handles that as well (30 - 0 = 30).
  })
}

const systemRecordsComputed = computed(() =>
  systemRecords.value.map(item => ({
    ...item,
    dealershipNames: item.dealerships.map(d => d.name).join(', ')
  }))
)

// timeLeft: how many seconds until the next TOTP boundary from the moment we fetched
const timeLeft = computed(() => {
  const diff = expiryTimestamp.value - nowSeconds.value
  return diff > 0 ? diff : 0
})

// codesExpired: true if we've passed the boundary
const codesExpired = computed(() => timeLeft.value <= 0)

const inputRules = {
  id: {},
  username: { required },
  systemName: { required },
  password: {},
  totpSecretKey: {},
  baseUrl: { required },
  agreementUrl: {},
  dealerships: {},
  dealershipIds: { required }
}

const v$ = useVuelidate(inputRules, accountFormState)

const newAccountApiRequest = useApiRequest()
const editAccountApiRequest = useApiRequest()

async function submitForm() {
  const isFormCorrect = await v$.value.$validate()
  if (!isFormCorrect) {
    return
  }

  // Determine if is update or new
  if (isNewAccount.value) {
    const dataToPost = {
      username: accountFormState.value.username,
      systemName: accountFormState.value.systemName,
      password: accountFormState.value.password,
      totpSecretKey: accountFormState.value.totpSecretKey,
      baseUrl: accountFormState.value.baseUrl,
      dealerships: accountFormState.value.dealershipIds
    }

    try {
      newAccountApiRequest
        .send({
          method: 'POST',
          endpoint: '/v1/external-systems',
          data: JSON.stringify(dataToPost)
        })
        .then(response => {
          if (!response.success) {
            alertToast('Error', response.message, 'error')
          } else {
            alertToast('Success!', 'Account added', 'success')
            accountFormModalIsOpen.value = false
            accountFormHasUnsavedChanges.value = false
            accountFormState.value = accountFormStateDefault.value
            fetchData()
          }
        })
    } catch (error) {
      console.error('Failed to submit form', error)
      alertToast('Failed to submit form', error.message, 'error')
    }
    v$.value.$reset()
  } else {
    const dataToPost = {
      id: accountFormState.value.id,
      username: accountFormState.value.username,
      systemName: accountFormState.value.systemName,
      password: accountFormState.value.password,
      totpSecretKey: accountFormState.value.totpSecretKey,
      baseUrl: accountFormState.value.baseUrl,
      dealerships: accountFormState.value.dealershipIds
    }

    try {
      editAccountApiRequest
        .send({
          method: 'PATCH',
          endpoint: `/v1/external-systems/${accountFormState.value.id}`,
          data: JSON.stringify(dataToPost)
        })
        .then(response => {
          if (!response.success) {
            alertToast('Error', response.message, 'error')
          } else {
            alertToast('Success!', 'Account updated', 'success')

            const account = systemRecords.value.find(system => system.id == dataToPost.id)

            if (account) {
              const dealershipObjects = []
              accountFormState.value.dealershipIds.forEach(id => {
                const dealership = dealershipsAllowed.find(d => d.id == id)
                dealershipObjects.push(dealership)
              })

              Object.assign(account, {
                id: accountFormState.value.id,
                username: accountFormState.value.username,
                systemName: accountFormState.value.systemName,
                password: accountFormState.value.password,
                totpSecretKey: accountFormState.value.totpSecretKey,
                baseUrl: accountFormState.value.baseUrl,
                dealerships: dealershipObjects
              })
              accountFormModalIsOpen.value = false
              accountFormHasUnsavedChanges.value = false
              accountFormState.value = accountFormStateDefault.value
            }
          }
        })
    } catch (error) {
      console.error('Failed to submit form', error)
      alertToast('Failed to submit form', error.message, 'error')
    }
    v$.value.$reset()
  }
}

function resetVuelidate() {
  v$.value.$reset()
}
function resetPasswordVuelidate() {
  vPassword$.value.$reset()
}

watch(
  () => accountFormState.value,
  () => {
    if (isEqual(accountFormState.value, accountFormStateInit.value)) {
      accountFormHasUnsavedChanges.value = false
    } else {
      accountFormHasUnsavedChanges.value = true
    }
  },
  { deep: true }
)
</script>
